'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Calculator } from 'lucide-react';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { ProjectSubpageLayout } from '@/shared/ui';
import { useProjectEstimates } from '../hooks/use-estimate';
import { EstimateCard } from './EstimateCard';

interface ProjectEstimatesPageProps {
  projectId: string;
}

/**
 * Dedicated project Estimates page (FE3) — mirrors `ProjectAlbumsPage`. Lists
 * every cost estimate across all of the project's concepts (in-progress first,
 * then newest, per the server order). Each card opens its owning concept's
 * Estimate tab.
 */
export function ProjectEstimatesPage({ projectId }: ProjectEstimatesPageProps) {
  const t = useTranslations('common.estimate');
  const tProject = useTranslations('project');

  const { data: estimates = [], isLoading } = useProjectEstimates(projectId);

  return (
    <ProjectSubpageLayout
      projectId={projectId}
      backLabel={tProject('backToProject')}
      title={t('page.title')}
    >
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />
          ))}
        </div>
      ) : estimates.length === 0 ? (
        <EmptyState
          icon={<Calculator />}
          title={t('page.empty.title')}
          description={t.rich('page.empty.description', {
            link: (chunks) => (
              <Link
                href={`/project/${projectId}/concepts`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {chunks}
              </Link>
            ),
          })}
          centered
        />
      ) : (
        // One tile per row on phones, 2 / 3 / 4 as the viewport widens
        // (Android/Apple parity: adaptive grid with a ~270dp minimum tile).
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {estimates.map((estimate) => (
            <EstimateCard
              key={estimate.id}
              estimate={estimate}
              projectId={projectId}
              variant="list"
            />
          ))}
        </div>
      )}
    </ProjectSubpageLayout>
  );
}
